import { EventEntityType, EventType } from "../../../api/events";

export type StageScope = "CASE" | "ENTITY";
export type StageMeta = {
  scope: StageScope;
  entityType: EventEntityType;
};

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  [EventType.ACCOUNT_RED_FLAGGED]: "Red Flagging of Account",
  [EventType.CASE_OPENED]: "Case Opened",
  [EventType.CASE_ASSIGNED_TO_INVESTIGATOR]: "Investigating Officer Appointed",
  [EventType.INVESTIGATION_COMPLETED]: "Investigation Report Submitted",
  [EventType.ZFRC_MEETING_HELD]: "ZFRC Meeting",
  [EventType.FIC_REVIEW_MEETING_HELD]: "FIC for Decision On No-Fraud or Issuance of SCN",
  [EventType.SCN_ISSUED]: "Show Cause Notice Issued",
  [EventType.SCN_DELIVERED]: "Show Cause Notice Delivered",
  [EventType.SCN_RETURNED]: "Show Cause Notice Returned",
  [EventType.SCN_RESPONSE_RECEIVED]: "Show Cause Notice Response Received",
  [EventType.FIC_FRAUD_CLASSIFICATION_COMPLETED]: "FIC for Final Decision & Recommendation",
  [EventType.REASONED_ORDER_ISSUED]: "Reasoned Order Issued",
  [EventType.SARFAESI_NOTICE_13_2_ISSUED]: "SARFAESI Notice u/s 13(2) Issued",
  [EventType.SARFAESI_NOTICE_13_4_ISSUED]: "SARFAESI Notice u/s 13(4) Issued",
  [EventType.SECURITY_PHYSICALLY_POSSESSED]: "Security Physically Possessed",
  [EventType.RECOVERY_SUIT_FILED_IN_DRT]: "Recovery Suit Filed in DRT",
  [EventType.CASE_FILED_IN_NCLT]: "Case Filed in NCLT",
  [EventType.FMR1_FILED]: "FMR1 Filed",
  [EventType.COMPLAINT_LODGED_WITH_LEA]: "Complaint Lodged with LEAs",
  [EventType.FIR_FILED_WITH_LEA]: "FIR Filed with LEAs",
  [EventType.FRMD_NOTE_PRESENTED_TO_BOARD]: "FRMD Note Presented to Board & Committee",
  [EventType.BOARD_RECOMMENDATIONS_RECEIVED]: "Recommendations Received from Board & Committee",
  [EventType.CIRCULAR_ISSUED]: "Circular Issued - Verify the purpose",
  [EventType.FMR_WITHDRAWAL_COMMITTEE_CONSTITUTED]: "Committee for FMR Withdrawal Approval",
  [EventType.FMR_WITHDRAWAL_APPROVED]: "FMR Withdrawal Approved",
  [EventType.FMR_WITHDRAWN]: "FMR Withdrawn",
  [EventType.STATISTICAL_CLOSURE_COMMITTEE_CONSTITUTED]: "Committee for Statistical Closure of Case",
  [EventType.RBI_CLOSURE_COMMITTEE_CONSTITUTED]: "Committee for RBI Closure of Case",
  [EventType.STATISTICAL_CLOSURE_FROM_RBI_APPROVED]: "Statistical Closure of Case from SSM (RBI) Approved",
  [EventType.STATISTICAL_CLOSURE_RECORDED_IN_CIMS]: "Statistical Closure of Case in CIMS",
  [EventType.FINAL_CASE_CLOSURE_WITH_RBI]: "Final Closure of Case with RBI",
  [EventType.FMR_WITHDRAWAL_VERIFICATION_COMPLETED]: "FMR Withdrawal Verification Completed",
  [EventType.DATE_OF_OCCURENCE]: "Date of Occurrence",
  [EventType.DATE_OF_DETECTION]: "Date of Detection",
  [EventType.DATE_OF_CLASSIFICATION_CA_APPROVAL]: "Date of Classification (Approval from Competent Authority)",
  [EventType.HIA_RECOMMENDATIONS]: "HIA Recommendations",
  [EventType.ZI_RECOMMENDATIONS]: "ZI Recommendations",
  [EventType.CASE_DEALING_OFFICER_ASSIGNED]: "Dealing Officer Assigned",
  [EventType.IO_APPOINTING_AUTHORITY_ASSIGNED]: "IO Appointing Authority Assigned"
};

export const getEventTypeLabel = (eventType: EventType): string =>
  EVENT_TYPE_LABELS[eventType] ?? eventType;

export const STAGE_META: Record<EventType, StageMeta> = {
  [EventType.ACCOUNT_RED_FLAGGED]: {
    scope: "ENTITY",
    entityType: EventEntityType.LOAN_ACCOUNT,
  },
  [EventType.CASE_OPENED]: { scope: "CASE", entityType: EventEntityType.CASE },
  [EventType.CASE_ASSIGNED_TO_INVESTIGATOR]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.INVESTIGATION_COMPLETED]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.ZFRC_MEETING_HELD]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.FIC_REVIEW_MEETING_HELD]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.SCN_ISSUED]: {
    scope: "ENTITY",
    entityType: EventEntityType.PARTY_INVOLVED,
  },
  [EventType.SCN_DELIVERED]: {
    scope: "ENTITY",
    entityType: EventEntityType.PARTY_INVOLVED,
  },
  [EventType.SCN_RETURNED]: {
    scope: "ENTITY",
    entityType: EventEntityType.PARTY_INVOLVED,
  },
  [EventType.SCN_RESPONSE_RECEIVED]: {
    scope: "ENTITY",
    entityType: EventEntityType.PARTY_INVOLVED,
  },
  [EventType.FIC_FRAUD_CLASSIFICATION_COMPLETED]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.REASONED_ORDER_ISSUED]: {
    scope: "ENTITY",
    entityType: EventEntityType.PARTY_INVOLVED,
  },
  [EventType.SARFAESI_NOTICE_13_2_ISSUED]: {
    scope: "ENTITY",
    entityType: EventEntityType.LOAN_ACCOUNT,
  },
  [EventType.SARFAESI_NOTICE_13_4_ISSUED]: {
    scope: "ENTITY",
    entityType: EventEntityType.LOAN_ACCOUNT,
  },
  [EventType.SECURITY_PHYSICALLY_POSSESSED]: {
    scope: "ENTITY",
    entityType: EventEntityType.LOAN_ACCOUNT,
  },
  [EventType.RECOVERY_SUIT_FILED_IN_DRT]: {
    scope: "ENTITY",
    entityType: EventEntityType.LOAN_ACCOUNT,
  },
  [EventType.CASE_FILED_IN_NCLT]: {
    scope: "ENTITY",
    entityType: EventEntityType.LOAN_ACCOUNT,
  },
  [EventType.FMR1_FILED]: { scope: "CASE", entityType: EventEntityType.CASE },
  [EventType.COMPLAINT_LODGED_WITH_LEA]: {
    scope: "ENTITY",
    entityType: EventEntityType.LEA,
  },
  [EventType.FIR_FILED_WITH_LEA]: {
    scope: "ENTITY",
    entityType: EventEntityType.LEA,
  },
  [EventType.FRMD_NOTE_PRESENTED_TO_BOARD]: {
    scope: "ENTITY",
    entityType: EventEntityType.COMMITTEE,
  },
  [EventType.BOARD_RECOMMENDATIONS_RECEIVED]: {
    scope: "ENTITY",
    entityType: EventEntityType.COMMITTEE,
  },
  [EventType.CIRCULAR_ISSUED]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.FMR_WITHDRAWAL_VERIFICATION_COMPLETED]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.FMR_WITHDRAWAL_COMMITTEE_CONSTITUTED]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.FMR_WITHDRAWAL_APPROVED]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.FMR_WITHDRAWN]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.STATISTICAL_CLOSURE_COMMITTEE_CONSTITUTED]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.RBI_CLOSURE_COMMITTEE_CONSTITUTED]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.STATISTICAL_CLOSURE_FROM_RBI_APPROVED]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.STATISTICAL_CLOSURE_RECORDED_IN_CIMS]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.FINAL_CASE_CLOSURE_WITH_RBI]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.DATE_OF_OCCURENCE]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.DATE_OF_DETECTION]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.DATE_OF_CLASSIFICATION_CA_APPROVAL]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.HIA_RECOMMENDATIONS]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.ZI_RECOMMENDATIONS]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.CASE_DEALING_OFFICER_ASSIGNED]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
  [EventType.IO_APPOINTING_AUTHORITY_ASSIGNED]: {
    scope: "CASE",
    entityType: EventEntityType.CASE,
  },
};
